import { ChangeEvent, FunctionComponent, MouseEvent as ReactMouseEvent, useState } from "react";
import { useTranslation } from "react-i18next";
import { isSendableEmailStatus } from "@signalist/shared-types";

import { CircularProgress, Link } from "@mui/material";

import { faPaperPlane } from "@fortawesome/pro-regular-svg-icons";

import { Tooltip } from "@/components/Tooltip/Tooltip";
import { InputField } from "@/components/InputField/InputField";
import { Icon } from "@/components";
import { InfoCardPopover, InfoCardPopoverRow } from "@/components/InfoCardPopover";
import { ContactChannels, mapContactProspectToChannelData } from "@/components/ContactChannels";
import { useUpdateContactProspectMutation } from "@/pages/Private/redux/contact/contact.api";
import { useAddPersonToListMutation } from "@/pages/Private/redux/list/list.api";

import { AssignStatus } from "@/enum/list.enum";

import { ensureHttpProtocol } from "@/utils/url";

import { HideOnErrorImg } from "@/components/HideOnErrorImg/HideOnErrorImg";

import { resolveLeadReviewTarget } from "../../List/utils/resolveLeadReviewTarget";
import { CrmIcon } from "../../List/components/CrmIcon";
import { ContactProspect } from "../schema/contacts";

export const EditableContactNameColumn: FunctionComponent<{
	row: ContactProspect;
	handleCopyClick: (value: string) => void;
	setShowEdit: ({ id, name }: { id: number; name: string }) => void;
	showEdit: { id: number; name: string } | undefined;
	orderTitle: string;
}> = ({ row, handleCopyClick, orderTitle, setShowEdit, showEdit }) => {
	const { t } = useTranslation();
	const [value, setValue] = useState<string | undefined>(row?.name || "");
	const [updateProspect, { isLoading }] = useUpdateContactProspectMutation();
	const [addPersonToList, { isLoading: addPersonToListIsLoading }] = useAddPersonToListMutation();

	const canSendMessage =
		!!row.linkedinUrl || (!!row.email && isSendableEmailStatus(row.emailStatus));

	const handleSendMessage = async (e: ReactMouseEvent) => {
		e.preventDefault();
		e.stopPropagation();

		const draftListPerson = row.listPersons?.find((lp) => lp.assignStatus === AssignStatus.DRAFT);

		if (draftListPerson?.listId) {
			// Where the lead is reviewed follows the lead's own status, in one place — today this branch
			// only picks a DRAFT membership, so it resolves to Review-to-send, but the link stays right
			// if that ever widens (PREC-4103).
			const list = row.lists?.find((l) => l.id === draftListPerson.listId);
			const reviewHref = resolveLeadReviewTarget({
				assignStatus: draftListPerson.assignStatus as AssignStatus,
				list,
				prospect: { name: row.name },
			});

			if (reviewHref) {
				window.open(reviewHref, "_blank");
			}

			return;
		}

		try {
			if (row.linkedinUrl) {
				await addPersonToList({
					method: "linkedinUrl",
					linkedinUrl: row.linkedinUrl,
				}).unwrap();
			} else if (row.email) {
				await addPersonToList({
					method: "email",
					email: row.email,
				}).unwrap();
			} else {
				return;
			}
		} catch (error) {
			console.error("Failed to add prospect to compose list:", error);

			return;
		}

		const composeParams = new URLSearchParams();

		composeParams.set("searchValue", row.name || "");

		window.open(`/app/messages/compose?${composeParams.toString()}`, "_blank");
	};

	if (isLoading || addPersonToListIsLoading) {
		return <CircularProgress />;
	}

	return (
		<div className="relative flex w-full max-w-[250px] flew-row justify-between items-center">
			{showEdit && showEdit?.id === row.id && showEdit?.name === orderTitle ? (
				<div className="min-w-[250px] absolute z-10">
					<InputField
						autoFocus
						handleBlur={() => {
							setShowEdit({ id: 0, name: "" });
						}}
						handleChange={function (event: ChangeEvent<HTMLInputElement>): void {
							setValue(event.target.value);
						}}
						name={orderTitle}
						value={value || ""}
						onClick={(e: ReactMouseEvent) => {
							e.preventDefault();
							e.stopPropagation();
						}}
						onKeyDown={async (e) => {
							if (e.key === "Enter" && value) {
								await updateProspect({
									id: row.id,
									...{ name: value },
								}).unwrap();
								setShowEdit({ id: 0, name: "" });
							}
						}}
					/>
				</div>
			) : (
				<>
					<Tooltip
						content={
							<InfoCardPopover
								kind="lead"
								onCopy={handleCopyClick}
								rows={
									[
										{ label: "Name", value: row.name },
										{ label: "Job title", value: row.title },
										{
											label: "Persona category",
											value: row.scorePersonaCategoryResult?.result,
										},
										{ label: "Location", value: row.country },
										{
											label: "Persona prio",
											value: row.scorePersonaCategoryResult?.overallScore,
										},
									] satisfies InfoCardPopoverRow[]
								}
							/>
						}
					>
						<div className="cursor-pointer mr-1 min-w-0 flex-1 max-w-[140px] overflow-hidden flex items-center">
							{row.image ? (
								<HideOnErrorImg
									alt={row.name ?? ""}
									className="w-[24px] h-[24px] float-left rounded-full mr-1 shrink-0"
									referrerPolicy="no-referrer"
									src={row.image}
								/>
							) : (
								""
							)}
							<span
								className="block min-w-0 flex-1 truncate rounded-[5px] px-[3px] py-[1px] -mx-[3px] -my-[1px] hover:bg-gray-50 transition-colors"
								onContextMenu={(e) => {
									e.stopPropagation();
									e.preventDefault();
									setShowEdit({ id: row.id, name: orderTitle });
								}}
							>
								{row.name}
							</span>
						</div>
					</Tooltip>
					<div className="flex flex-row">
						{canSendMessage && (
							<Tooltip content={t("contacts.sendMessage")}>
								<button
									aria-label={t("contacts.sendMessage") || "Send a message"}
									className="mr-1 flex h-[20px] w-[20px] items-center justify-center cursor-pointer"
									type="button"
									onClick={handleSendMessage}
								>
									<Icon className="w-[16px] h-[16px] text-text-meta" icon={faPaperPlane} />
								</button>
							</Tooltip>
						)}

						{row.crmProspects?.map((crmProspect) => (
							<Tooltip
								key={crmProspect.id}
								content={
									<div className="flex flex-col gap-2">
										<span>Current Status: {crmProspect.currentStatus}</span>
										<span>Account Owner: {crmProspect.accountOwner}</span>
										<span>Reference ID: {crmProspect.referenceId}</span>
										{crmProspect.syncStatus && <span>Sync Status: {crmProspect.syncStatus}</span>}
										{crmProspect.syncError && <span>Sync Error: {crmProspect.syncError}</span>}
									</div>
								}
								disabled={!crmProspect.url && !crmProspect.syncStatus && !crmProspect.syncError}
							>
								{crmProspect.url ? (
									<Link href={ensureHttpProtocol(crmProspect.url ?? "")} target="_blank">
										<CrmIcon
											crmType={crmProspect.crmType}
											syncError={crmProspect.syncError}
											syncStatus={crmProspect.syncStatus}
											url={crmProspect.url ?? ""}
										/>
									</Link>
								) : (
									<CrmIcon
										crmType={crmProspect.crmType}
										syncError={crmProspect.syncError}
										syncStatus={crmProspect.syncStatus}
										url={crmProspect.url ?? ""}
									/>
								)}
							</Tooltip>
						))}

						<ContactChannels
							data={mapContactProspectToChannelData(row)}
							domain="contact"
							onCopy={handleCopyClick}
						/>
					</div>
				</>
			)}
		</div>
	);
};
